import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { businessApi } from '@/lib/api';
import { useBusinessNav } from '@/hooks/useBusinessNav';

export function StripeReturn() {
  const { businessId } = useParams<{ businessId: string }>();
  const { nav } = useBusinessNav();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {

    if (!businessId) {
      setStatus('error');
      setMessage('Missing business ID. Please try again from Settings.');
      return;
    }

    // Check account status
    businessApi.getFeatureFlags(businessId).then((response) => {
      if (response.error) {
        setStatus('error');
        setMessage('Failed to check account status. Please try again.');
        return;
      }

      if (response.data?.payments_setup_complete) {
        setStatus('success');
        setMessage('Your Stripe account is now connected! You can accept payments on your invoices.');
      } else if (response.data?.stripe_account_id) {
        setStatus('success');
        setMessage('Almost there! Your Stripe account is set up but may still be processing. Check Settings for status.');
      } else {
        setStatus('error');
        setMessage('Stripe setup was not completed. Please try again from Settings.');
      }

      // Redirect to payment settings after a short delay
      setTimeout(() => {
        nav('/settings/payments');
      }, 3000);
    });
  }, [businessId, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center text-center space-y-4">
            {status === 'loading' && (
              <>
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">Checking account status...</h2>
                  <p className="text-sm text-muted-foreground">
                    Please wait while we verify your Stripe connection.
                  </p>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                  <Check className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">Stripe Connected!</h2>
                  <p className="text-sm text-muted-foreground">{message}</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Redirecting to Payment Processing...
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">Setup Incomplete</h2>
                  <p className="text-sm text-muted-foreground">{message}</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Redirecting to Payment Processing...
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
